import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Briefcase, Building2, CalendarClock, ChevronUp, Circle, ClipboardCheck, Eye, FileText,
  Send, Sparkles,
} from "lucide-react";
import { GUIDE_STEPS } from "../../store/slices/firstRunGuideSlice";
import { ClickShield } from "./CoachMark";

// The icon in the current step's eyebrow.
const STEP_ICONS = {
  company_profile: Building2,
  create_job: Briefcase,
  screen_resume: FileText,
  schedule_interview: CalendarClock,
  track_interview: ClipboardCheck,
};

// The "Getting set up" pop-up: a card in the middle of the screen showing the
// current step (a picture, then the words), joined by a line to the thing to
// click (a header tab, a button, or a group of status filters). That target is ringed
// and the rest of the page is lightly dimmed. The dim ignores the mouse, so the
// page stays usable; the card itself is clickable.

const STEP_COPY = {
  company_profile: {
    title: "Complete your company profile",
    hint: "Candidates see your company details before they apply, so a complete profile builds trust from the first click. Open Settings in the sidebar to fill it in.",
  },
  create_job: { title: "Create a job role", hint: "Describe the role you're hiring for. Takes about 2 minutes." },
  screen_resume: {
    title: "Screen a resume",
    hint: "Upload a CV and see how well it matches the role.",
  },
  schedule_interview: {
    title: "Schedule an interview",
    hint: "Send a candidate their AI interview invite.",
    dockText: "Schedule the interview when you're ready",
  },
  track_interview: {
    title: "Evaluate the report",
    hint: "Follow each interview as it moves along. Once it's completed, you can see the report here.",
    substeps: ["Scheduled", "In Progress", "Completed"],
  },
};

const PAD = 6; // room around the ringed target
const GAP = 24; // how far the line runs outside the card before turning
const RADIUS = 16; // corner radius of the line's bends
// Lighter than it would be alone: the blur behind it does half the work.
const SCRIM = "rgba(17, 12, 28, 0.4)";
const ACCENT = "#ec4899";

const unionBox = (els) => {
  const rects = els.map((el) => el.getBoundingClientRect()).filter((r) => r.width || r.height);
  if (!rects.length) return null;
  const left = Math.min(...rects.map((r) => r.left));
  const top = Math.min(...rects.map((r) => r.top));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  return { left, top, right, bottom, width: right - left, height: bottom - top };
};

const sameBox = (a, b) =>
  a && b && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;

// Follows one or more elements' combined box on every frame (scrolling,
// resizing, layout shifts). `els` must be a stable array (memoised by callers).
const useBox = (els) => {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!els.length) {
      setRect(null);
      return undefined;
    }
    let frame;
    const track = () => {
      const next = unionBox(els);
      setRect((prev) => (sameBox(prev, next) ? prev : next));
      frame = requestAnimationFrame(track);
    };
    track();
    return () => cancelAnimationFrame(frame);
  }, [els]);
  return rect;
};

// How far the card sits from the target, and from the screen edge.
const OFFSET = 56;
const MARGIN = 16;

// Where to put the card: right next to the target, so the line between them is
// short. A header tab gets the card below it; anything else gets it to the
// right, then the left, then below, then above -- whichever fits on screen.
// The card is pulled up so the target lines up with its upper part, where the
// line joins it. Returns null (stay centred) when nothing fits.
function placeCard(target, size) {
  if (!target || !size) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const { width: W, height: H } = size;
  const clampX = (x) => Math.min(Math.max(x, MARGIN), vw - W - MARGIN);
  const clampY = (y) => Math.min(Math.max(y, MARGIN), vh - H - MARGIN);
  const midY = clampY(target.top + target.height / 2 - 72);
  const midX = clampX(target.left + target.width / 2 - W * 0.25);
  const spots = {
    right: target.right + PAD + OFFSET + W <= vw - MARGIN && { left: target.right + PAD + OFFSET, top: midY },
    left: target.left - PAD - OFFSET - W >= MARGIN && { left: target.left - PAD - OFFSET - W, top: midY },
    below: target.bottom + PAD + OFFSET + H <= vh - MARGIN && { left: midX, top: target.bottom + PAD + OFFSET },
    above: target.top - PAD - OFFSET - H >= MARGIN && { left: midX, top: target.top - PAD - OFFSET - H },
  };
  const order = target.top < vh * 0.15 ? ["below", "right", "left", "above"] : ["right", "left", "below", "above"];
  const side = order.find((s) => spots[s]);
  return side ? spots[side] : null;
}

// From the card to the target. With the card placed beside or under the target
// this is one short curve; the routed path below is only for a centred card
// the target sits well away from. Returns the path and its end points.
function connectorPath(target, row, card) {
  const tx = target.left + target.width / 2;
  const goLeft = tx < card.left + card.width / 2;
  const sx = goLeft ? card.left : card.right;
  const sy = row.top + row.height / 2;
  const gx = goLeft ? card.left - GAP : card.right + GAP;
  const above = target.bottom < card.top;
  const below = target.top > card.bottom;
  const r = RADIUS;
  const dirX = goLeft ? -1 : 1;

  if (!above && !below) {
    // Target beside the card: one curve from the card's upper edge across.
    const ex = goLeft ? target.right + PAD : target.left - PAD;
    const ey = target.top + target.height / 2;
    const y0 = Math.min(card.top + 48, card.bottom - 24);
    return {
      d: `M ${sx} ${y0} C ${(sx + ex) / 2} ${y0}, ${(sx + ex) / 2} ${ey}, ${ex} ${ey}`,
      start: [sx, y0],
      end: [ex, ey],
    };
  }

  if (tx > card.left && tx < card.right) {
    // Target straight above or below the card: one curve between the two edges.
    const x0 = Math.min(Math.max(tx, card.left + 32), card.right - 32);
    const y0 = above ? card.top : card.bottom;
    const ey = above ? target.bottom + PAD : target.top - PAD;
    const my = (y0 + ey) / 2;
    return { d: `M ${x0} ${y0} C ${x0} ${my}, ${tx} ${my}, ${tx} ${ey}`, start: [x0, y0], end: [tx, ey] };
  }

  const busY = above ? target.bottom + PAD + 18 : target.top - PAD - 18;
  const ey = above ? target.bottom + PAD : target.top - PAD;
  const dirY = above ? -1 : 1;
  const towardTarget = tx > gx ? 1 : -1;
  if (Math.abs(sy - busY) < 2 * r || Math.abs(tx - gx) < 2 * r) {
    return { d: `M ${sx} ${sy} C ${gx} ${sy}, ${tx} ${busY}, ${tx} ${ey}`, start: [sx, sy], end: [tx, ey] };
  }
  const d = [
    `M ${sx} ${sy}`,
    `H ${gx - dirX * r}`,
    `Q ${gx} ${sy} ${gx} ${sy + dirY * r}`,
    `V ${busY - dirY * r}`,
    `Q ${gx} ${busY} ${gx + towardTarget * r} ${busY}`,
    `H ${tx - towardTarget * r}`,
    `Q ${tx} ${busY} ${tx} ${busY + dirY * r}`,
    `V ${ey}`,
  ].join(" ");
  return { d, start: [sx, sy], end: [tx, ey] };
}

// The connecting line: a thin, solid, bright pink line with a tight glow that
// hugs it (a lit wire, not a halo), and a small dot where it meets the target.
// A brighter pulse runs along it from the card to the target, on a loop, so
// the eye follows it to the thing to click. pathLength="100" makes the pulse
// the same share of the line however long the line is.
const LINE = "#ff6ec7";
const Wire = ({ d, end }) => (
  <>
    <defs>
      {/* userSpaceOnUse: a straight vertical or horizontal line has a zero-width
          box, and a filter sized to the box would clip the line away. */}
      <filter id="gh-guide-glow" filterUnits="userSpaceOnUse" x="0" y="0" width="100%" height="100%">
        <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor={LINE} floodOpacity="1" />
      </filter>
      <filter id="gh-guide-pulse-glow" filterUnits="userSpaceOnUse" x="0" y="0" width="100%" height="100%">
        <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={LINE} floodOpacity="1" />
      </filter>
    </defs>
    <path
      d={d}
      fill="none"
      stroke={LINE}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      filter="url(#gh-guide-glow)"
    />
    {/* The running pulse: a short bright dash with a white core. */}
    <path
      d={d}
      pathLength="100"
      fill="none"
      stroke={LINE}
      strokeWidth="5"
      strokeLinecap="round"
      strokeDasharray="7 93"
      className="gh-guide-run"
      filter="url(#gh-guide-pulse-glow)"
    />
    <path
      d={d}
      pathLength="100"
      fill="none"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="4 96"
      className="gh-guide-run gh-guide-run-core"
    />
    <circle cx={end[0]} cy={end[1]} r="3.5" fill={LINE} filter="url(#gh-guide-glow)" />
  </>
);

const roundedRect = (x, y, w, h, r) =>
  `M${x + r},${y} H${x + w - r} A${r},${r} 0 0 1 ${x + w},${y + r} V${y + h - r} ` +
  `A${r},${r} 0 0 1 ${x + w - r},${y + h} H${x + r} A${r},${r} 0 0 1 ${x},${y + h - r} ` +
  `V${y + r} A${r},${r} 0 0 1 ${x + r},${y} Z`;

// Blurs the page behind the guide, with a hole cut out for the target so the
// thing to click stays sharp. An SVG can only paint over the page, not blur
// it, so this is a backdrop-filter layer masked by an even-odd path: the whole
// screen minus the target's rounded box. The card sits above it and needs no
// hole.
const BlurLayer = ({ target }) => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  let d = `M0,0 H${w} V${h} H0 Z`;
  if (target) {
    d += ` ${roundedRect(target.left - PAD, target.top - PAD, target.width + PAD * 2, target.height + PAD * 2, 10)}`;
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'><path fill-rule='evenodd' fill='black' d='${d}'/></svg>`;
  const mask = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1299,
        pointerEvents: "none",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
        maskImage: mask,
        WebkitMaskImage: mask,
        maskSize: "100% 100%",
        WebkitMaskSize: "100% 100%",
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
      }}
    />
  );
};

// block: false keeps the page sharp and clickable while the card is open (no
// blur, dim or click shield); only the ring and the line to the target show.
const Overlay = ({ target, card, row, pulse = false, block = true }) => (
  <>
  {card && block && <BlurLayer target={target} />}
  {/* While the card is open only the ringed step and the card can be clicked */}
  {card && block && <ClickShield holes={[target]} pad={PAD} />}
  <svg
    aria-hidden="true"
    style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", pointerEvents: "none", zIndex: 1300 }}
  >
    <style>{`
      @keyframes gh-guide-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      .gh-guide-pulse { animation: gh-guide-pulse 1.6s ease-in-out infinite; }
      /* Negative offset moves the dash forward: card -> target. */
      @keyframes gh-guide-run { from { stroke-dashoffset: 7; } to { stroke-dashoffset: -93; } }
      .gh-guide-run { animation: gh-guide-run 2.2s cubic-bezier(0.45, 0, 0.25, 1) infinite; }
      /* The core is shorter, so it starts 1.5 later to sit in the dash's middle. */
      .gh-guide-run-core { animation-name: gh-guide-run-core; }
      @keyframes gh-guide-run-core { from { stroke-dashoffset: 5.5; } to { stroke-dashoffset: -94.5; } }
      @media (prefers-reduced-motion: reduce) {
        .gh-guide-pulse { animation: none; }
        .gh-guide-run { animation: none; opacity: 0; }
      }
    `}</style>
    {card && block && (
      <>
        <defs>
          <mask id="gh-guide-holes">
            <rect width="100%" height="100%" fill="white" />
            {target && (
              <rect
                x={target.left - PAD}
                y={target.top - PAD}
                width={target.width + PAD * 2}
                height={target.height + PAD * 2}
                rx="10"
                fill="black"
              />
            )}
            <rect x={card.left} y={card.top} width={card.width} height={card.height} rx="16" fill="black" />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill={SCRIM} mask="url(#gh-guide-holes)" />
      </>
    )}
    {/* The target, lifted: a soft glow, a gradient ring, and a thin light
        inner ring between the ring and the element. */}
    {target && (
      <g className={pulse ? "gh-guide-pulse" : undefined}>
        <defs>
          <linearGradient id="gh-guide-ring" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={ACCENT} />
            <stop offset="100%" stopColor={LINE} />
          </linearGradient>
          <filter id="gh-guide-ring-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="7" />
          </filter>
        </defs>
        <rect
          x={target.left - PAD}
          y={target.top - PAD}
          width={target.width + PAD * 2}
          height={target.height + PAD * 2}
          rx="12"
          fill="none"
          stroke={ACCENT}
          strokeOpacity="0.55"
          strokeWidth="10"
          filter="url(#gh-guide-ring-glow)"
        />
        <rect
          x={target.left - PAD}
          y={target.top - PAD}
          width={target.width + PAD * 2}
          height={target.height + PAD * 2}
          rx="12"
          fill="none"
          stroke="url(#gh-guide-ring)"
          strokeWidth="3"
        />
        <rect
          x={target.left - PAD + 3}
          y={target.top - PAD + 3}
          width={target.width + PAD * 2 - 6}
          height={target.height + PAD * 2 - 6}
          rx="9"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.9"
          strokeWidth="1.5"
        />
      </g>
    )}
    {target && card && row && <Wire {...connectorPath(target, row, card)} />}
  </svg>
  </>
);

/** Just the pink ring around the target, shown after the pop-up is closed.
 *  `pulse` makes it gently breathe, for pages where nothing else is shown. */
export const TargetRing = ({ targets, pulse = false }) => {
  const target = useBox(targets);
  return target ? <Overlay target={target} pulse={pulse} /> : null;
};

/** Collapsed form of the guide for pages where the user is working (e.g.
 *  reading screening results): a small pill in the corner that covers nothing.
 *  Clicking it opens the full pop-up. */
export const GuideDock = ({ activeKey, onOpen }) => {
  const stepNumber = GUIDE_STEPS.indexOf(activeKey) + 1;
  const copy = STEP_COPY[activeKey];
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Open the setup guide"
      className="fixed bottom-6 right-6 z-[1301] flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-full bg-white dark:bg-[#1B1524] border border-pink-200 dark:border-pink-500/30 shadow-xl text-left hover:shadow-2xl transition-shadow"
    >
      <span className="flex-none w-2 h-2 rounded-full bg-pink-500" />
      <span className="flex flex-col leading-tight">
        <span className="text-[11px] font-medium uppercase tracking-wider text-pink-500">
          Step {stepNumber} of {GUIDE_STEPS.length}
        </span>
        <span className="text-sm text-gray-800 dark:text-gray-100">
          {copy.dockText || copy.title}
        </span>
      </span>
      <ChevronUp className="flex-none w-4 h-4 text-gray-400" />
    </button>
  );
};

// ---------------------------------------------------------------------------
// The picture at the top of the card: a small mock of what the step produces,
// so the step reads at a glance before the words. Sample names and numbers are
// illustration only.
// ---------------------------------------------------------------------------
const MockCard = ({ children, className = "" }) => (
  <div
    className={`w-[300px] max-w-[85%] rounded-xl bg-white dark:bg-[#251d31] border border-pink-100 dark:border-pink-500/20 shadow-[0_8px_24px_rgba(236,72,153,0.12)] px-3.5 py-3 ${className}`}
  >
    {children}
  </div>
);

const Bar = ({ w }) => <span className="block h-1.5 rounded-full bg-gray-200 dark:bg-white/10" style={{ width: w }} />;

const Chip = ({ children }) => (
  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-pink-50 text-pink-600 dark:bg-pink-500/15 dark:text-pink-300">
    {children}
  </span>
);

const ScoreRing = ({ value }) => {
  const r = 15;
  const c = 2 * Math.PI * r;
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" className="flex-none">
      <defs>
        <linearGradient id="gh-guide-score" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ec4899" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r={r} fill="none" stroke="currentColor" className="text-gray-200 dark:text-white/10" strokeWidth="4" />
      <circle
        cx="20" cy="20" r={r} fill="none" stroke="url(#gh-guide-score)" strokeWidth="4" strokeLinecap="round"
        strokeDasharray={`${(value / 100) * c} ${c}`} transform="rotate(-90 20 20)"
      />
      <text x="20" y="23.5" textAnchor="middle" className="fill-gray-800 dark:fill-gray-100" style={{ fontSize: 10, fontWeight: 700 }}>
        {value}%
      </text>
    </svg>
  );
};

const STEP_GRAPHICS = {
  company_profile: () => (
    <MockCard>
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-blue-500 flex items-center justify-center">
          <Building2 className="w-4 h-4 text-white" />
        </span>
        <span className="flex flex-col gap-0.5">
          <span className="text-[12px] font-semibold text-gray-800 dark:text-gray-100">Your company</span>
          <span className="text-[10px] text-gray-400">About · Industry · Size</span>
        </span>
      </div>
      <div className="mt-2.5 flex flex-col gap-1.5">
        <Bar w="100%" />
        <Bar w="86%" />
        <Bar w="64%" />
      </div>
      <span className="mt-2.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
        <Eye className="w-3 h-3" /> Visible to candidates
      </span>
    </MockCard>
  ),
  create_job: () => (
    <MockCard>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-gray-800 dark:text-gray-100">Senior Product Designer</span>
        <span className="text-[10px] text-gray-400">Full-time</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        <Chip>Figma</Chip>
        <Chip>UX research</Chip>
        <Chip>Prototyping</Chip>
      </div>
      <span className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold text-white bg-gradient-to-r from-pink-500 to-blue-500">
        <Sparkles className="w-3 h-3" /> Role ready in 2 min
      </span>
    </MockCard>
  ),
  screen_resume: () => (
    <MockCard className="flex items-center gap-3">
      <span className="flex-none w-9 h-11 rounded-md border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 flex flex-col gap-1 p-1.5">
        <Bar w="100%" />
        <Bar w="70%" />
        <Bar w="85%" />
      </span>
      <span className="flex-1 min-w-0 flex flex-col gap-1">
        <span className="text-[12px] font-semibold text-gray-800 dark:text-gray-100">Priya Sharma</span>
        <span className="flex flex-wrap gap-1">
          <Chip>Skills ✓</Chip>
          <Chip>Experience ✓</Chip>
        </span>
      </span>
      <ScoreRing value={86} />
    </MockCard>
  ),
  schedule_interview: () => (
    <MockCard>
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-blue-400 text-white text-[11px] font-bold flex items-center justify-center">
          AK
        </span>
        <span className="flex-1 flex flex-col gap-0.5">
          <span className="text-[12px] font-semibold text-gray-800 dark:text-gray-100">Arjun Kumar</span>
          <span className="text-[10px] text-gray-400">AI interview invite</span>
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
          <Send className="w-3 h-3" /> Sent
        </span>
      </div>
      <div className="mt-2.5 flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
        <CalendarClock className="w-3.5 h-3.5 text-pink-500" /> Invite sent today
      </div>
    </MockCard>
  ),
  track_interview: () => (
    <MockCard>
      <div className="flex items-center justify-between">
        {["Scheduled", "In Progress", "Completed"].map((label, i) => (
          <React.Fragment key={label}>
            {i > 0 && <span className="flex-1 h-px mx-1 bg-gradient-to-r from-pink-300 to-blue-300" />}
            <span className="flex flex-col items-center gap-1">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  i === 2 ? "bg-blue-500 animate-pulse" : "bg-pink-500"
                }`}
              />
              <span className="text-[9px] text-gray-500 dark:text-gray-400 whitespace-nowrap">{label}</span>
            </span>
          </React.Fragment>
        ))}
      </div>
      <div className="mt-2.5 flex items-end gap-1 h-7">
        {[60, 85, 45, 70, 92].map((h, i) => (
          <span key={i} className="flex-1 rounded-sm bg-gradient-to-t from-pink-400 to-blue-400" style={{ height: `${h}%` }} />
        ))}
      </div>
    </MockCard>
  ),
};

const GettingStartedPopup = ({ steps, activeKey, targets, ctaLabel, onCta, onClose, onSkip, blockPage = true }) => {
  const cardRef = useRef(null);
  const rowRef = useRef(null);
  const [cardEl, setCardEl] = useState(null);
  const [rowEl, setRowEl] = useState(null);
  useEffect(() => {
    setCardEl(cardRef.current);
    setRowEl(rowRef.current);
  }, [activeKey]);

  const cardEls = useMemo(() => (cardEl ? [cardEl] : []), [cardEl]);
  const rowEls = useMemo(() => (rowEl ? [rowEl] : []), [rowEl]);
  const target = useBox(targets);
  const card = useBox(cardEls);
  const row = useBox(rowEls);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const doneCount = GUIDE_STEPS.filter((key) => steps[key]).length;
  const index = GUIDE_STEPS.indexOf(activeKey);
  const copy = STEP_COPY[activeKey] || {};
  const Icon = STEP_ICONS[activeKey] || Circle;
  const Graphic = STEP_GRAPHICS[activeKey];

  // Next to the target when there is one and it fits; centred otherwise.
  // While there is a target but either box has not been measured yet (the
  // first paint after the pop-up mounts), it stays hidden, so it never flashes
  // in the middle before moving next to the target.
  const spot = placeCard(target, card && { width: card.width, height: card.height });
  const measuring = targets.length > 0 && (!target || !card);
  const placement = spot
    ? { left: spot.left, top: spot.top }
    : measuring
    ? { visibility: "hidden" }
    : undefined;

  // One step at a time: a picture of what the step produces, then the step,
  // then Skip guide. The thin bar along the top edge is how far along the
  // guide is.
  return (
    <>
      <Overlay target={target} card={measuring ? null : card} row={row} block={blockPage} />
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby="gh-guide-title"
        style={placement}
        className={`fixed ${
          spot ? "" : "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        } z-[1302] w-[460px] max-w-[calc(100vw-32px)] rounded-2xl overflow-hidden bg-white dark:bg-[#1B1524] border border-gray-200 dark:border-[#3a2f4a] shadow-2xl flex flex-col`}
      >
        <div
          className="h-1 w-full bg-gray-100 dark:bg-white/10"
          role="progressbar"
          aria-label="Setup progress"
          aria-valuemin={0}
          aria-valuemax={GUIDE_STEPS.length}
          aria-valuenow={doneCount}
        >
          <div
            className="h-full bg-gradient-to-r from-pink-500 to-blue-500 transition-all duration-500"
            style={{ width: `${(doneCount / GUIDE_STEPS.length) * 100}%` }}
          />
        </div>

        <div className="relative h-[150px] flex items-center justify-center bg-gradient-to-br from-pink-50 via-white to-blue-50 dark:from-pink-500/10 dark:via-transparent dark:to-blue-500/10 border-b border-gray-100 dark:border-white/10">
          {Graphic && <Graphic />}
        </div>

        <div ref={rowRef} className="px-5 pt-4 pb-2 flex flex-col gap-2">
          {/* Eyebrow: which guide this is, and where this step sits. */}
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <span className="flex-none w-7 h-7 rounded-full bg-pink-100 dark:bg-pink-500/20 flex items-center justify-center">
                <Icon className="w-3.5 h-3.5 text-pink-500" strokeWidth={2.25} />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-pink-500">
                Getting set up
              </span>
            </span>
            <span className="text-xs font-medium text-gray-400 dark:text-gray-500 tabular-nums">
              {index + 1} / {GUIDE_STEPS.length}
            </span>
          </div>
          <h2 id="gh-guide-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {copy.title}
          </h2>
          <p className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">{copy.hint}</p>
          {copy.substeps && (
            <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
              {copy.substeps.map((label, i) => (
                <li key={label} className="flex items-center gap-1.5">
                  {i > 0 && <span aria-hidden="true" className="text-gray-400">→</span>}
                  <span className="px-2 py-0.5 rounded-full bg-white dark:bg-white/10 border border-pink-200 dark:border-pink-500/30">
                    {label}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {ctaLabel && (
            <button
              type="button"
              onClick={onCta}
              className="self-start mt-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white bg-gradient-to-r from-pink-500 to-blue-500"
            >
              {ctaLabel}
            </button>
          )}
        </div>

        <div className="px-5 pt-2 pb-4">
          <button
            type="button"
            onClick={onSkip}
            className="text-[13px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            Skip guide
          </button>
        </div>
      </div>
    </>
  );
};

export default GettingStartedPopup;
